import { useQuery, UseQueryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useApiClient } from "../client";
import { Template } from "@/page-components/post-generator/core/template.schemas";
import { CreateTemplateRequest, UpdateTemplateRequest, TemplatesFilterParams, TemplatesListPaginatedResponse, TemplateCategoriesResponse } from "../interfaces/templates.interface";
import { userProfileKeys } from "./user-profile.requests";
import { Context } from "@/util-components/root/context";


/**
 * Sends a Templates-2 create request (POST /templates/template)
 * and returns the validated template as JSON (echo).
 */
export function useTemplates2Create() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "create"],
    mutationFn: (payload: CreateTemplateRequest) =>
      apiRequest<Template>("templates/template", "POST", payload),
  });
}

/**
 * Fetches potential default templates
 */
export function useTemplates2GetPotentialDefaults(
  entityType: string,
  options?: Omit<UseQueryOptions<Template[]>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "defaults", "potential", entityType],
    enabled: options?.enabled ?? false,
    queryFn: () => {
      const queryParams = new URLSearchParams();
      queryParams.append('entityType', entityType);
      return apiRequest<Template[]>(`templates/defaults/potential?${queryParams.toString()}`);
    },
    ...options,
  });
}

/**
 * Fetches configured default templates (GET /templates/defaults/config)
 */
export function useTemplates2GetDefaults(
  options?: Omit<UseQueryOptions<Record<string, string>>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "defaults"],
    enabled: options?.enabled ?? false,
    queryFn: () => apiRequest<Record<string, string>>("templates/defaults/config"),
    ...options,
  });
}

/**
 * Assigns a default template (POST /templates/defaults/assign)
 */
export function useTemplates2AssignDefault() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "assignDefault"],
    mutationFn: (payload: { templateType: string; templateId?: string }) =>
      apiRequest<void>("templates/defaults/assign", "POST", payload),
  });
}

/**
 * Duplicates a store template (Internal User only) (POST /templates/store/:id/duplicate)
 */
export function useTemplates2DuplicateStore() {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["templates", "duplicateStore"],
    mutationFn: (id: string) =>
      apiRequest<Template>(`templates/store/${id}/duplicate`, "POST"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
    },
  });
}

/**
 * Duplicates a customer template (POST /templates/:id/duplicate)
 */
export function useTemplates2Duplicate() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "duplicate"],
    mutationFn: (id: string) =>
      apiRequest<Template>(`templates/${id}/duplicate`, "POST"),
  });
}


/**
 * Fetches paginated templates list with filtering (GET /templates)
 * Disabled by default; trigger via refetch() or set enabled: true
 */
export function useTemplates2FindAll(
  params?: TemplatesFilterParams,
  options?: Omit<UseQueryOptions<TemplatesListPaginatedResponse>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "findAll", params],
    enabled: options?.enabled ?? false,
    queryFn: () => {
      const queryParams = new URLSearchParams();
      if (params?.textSearch) queryParams.append('textSearch', params.textSearch);
      
      if (params?.categories) {
        if (params.categories.length === 0) {
          queryParams.append('categories', '');
        } else {
          params.categories.forEach(c => queryParams.append('categories', c));
        }
      }

      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.direction) queryParams.append('direction', params.direction);

      const queryString = queryParams.toString();
      const url = `templates${queryString ? `?${queryString}` : ''}`;

      return apiRequest<TemplatesListPaginatedResponse>(url);
    },
    staleTime: 5000,
    gcTime: 5000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Fetches all template categories for the organization (GET /templates/categories)
 */
export function useTemplates2GetCategories(
  options?: Omit<UseQueryOptions<TemplateCategoriesResponse>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "categories"],
    enabled: options?.enabled ?? false,
    queryFn: () => apiRequest<TemplateCategoriesResponse>("templates/categories"),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Fetches all store template categories (GET /templates/store/categories)
 */
export function useTemplates2GetStoreCategories(
  options?: Omit<UseQueryOptions<TemplateCategoriesResponse>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "storeCategories"],
    enabled: options?.enabled ?? false,
    queryFn: () => apiRequest<TemplateCategoriesResponse>("templates/store/categories"),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Hook to fetch a single template by ID
 * Returns a function that uses the API client, suitable for non-React-Query usage
 */
export function useFetchTemplate() {
  const { apiRequest } = useApiClient();
  
  return async (id: string): Promise<Template> => {
    return apiRequest<Template>(`templates/${id}`, "GET");
  };
}

/**
 * Hook to save a template
 * Returns a function that uses the API client, suitable for non-React-Query usage
 */
export function useSaveTemplate() {
  const { apiRequest } = useApiClient();
  
  return async (template: Template & { thumbnailData?: string }, options?: { incrementVersion?: boolean }): Promise<Template> => {
    const isUpdate = template.id && template.id !== '';
    const url = isUpdate ? `templates/${template.id}` : `templates/template`;
    const method = isUpdate ? "PATCH" : "POST";
    
    const payload = isUpdate ? {
      title: template.title,
      aspectRatio: template.aspectRatio,
      canvas: template.canvas,
      elements: template.elements,
      slots: template.slots,
      meta: template.meta,
      thumbnailData: template.thumbnailData,
      incrementVersion: options?.incrementVersion
    } : template;

    return apiRequest<Template>(url, method, payload);
  };
}

/**
 * Fetches a single template by ID (GET /templates/:id)
 * Disabled by default; trigger via refetch() or set enabled: true
 */
export function useTemplates2FindOne(
  id: string,
  options?: Omit<UseQueryOptions<Template>, "queryKey" | "queryFn"> & { skipGlobalErrorHandler?: boolean }
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "findOne", id],
    enabled: options?.enabled ?? false,
    queryFn: () => apiRequest<Template>(`templates/${id}`, "GET", undefined, undefined, {
      skipGlobalErrorHandler: options?.skipGlobalErrorHandler,
      throwOnError: true
    }),
    ...options,
  });
}

/**
 * Updates an existing template (PATCH /templates/:id)
 */
export function useTemplates2Update() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "update"],
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTemplateRequest }) =>
      apiRequest<Template>(`templates/${id}`, "PATCH", payload),
  });
}

/**
 * Deletes a template by ID (DELETE /templates/:id)
 */
export function useTemplates2Delete() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "delete"],
    mutationFn: (id: string) => apiRequest<void>(`templates/${id}`, "DELETE"),
  });
}

/**
 * Bulk deletes templates by IDs (POST /templates/bulk-delete)
 */
export function useTemplates2BulkDelete() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "bulkDelete"],
    mutationFn: (ids: string[]) =>
      apiRequest<void>("templates/bulk-delete", "POST", { ids }),
  });
}

// ========================================================================================================
// STORE TEMPLATES
// ========================================================================================================

/**
 * Creates a new store template (Internal User only) (POST /templates/store)
 */
export function useTemplates2CreateStore() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "createStore"],
    mutationFn: (payload: CreateTemplateRequest) =>
      apiRequest<Template>("templates/store", "POST", payload),
  });
}

/**
 * Fetches paginated store templates list (Customer) (GET /templates/store/list)
 */
export function useTemplates2FindAllStore(
  params?: TemplatesFilterParams,
  options?: Omit<UseQueryOptions<TemplatesListPaginatedResponse>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "findAllStore", params],
    enabled: options?.enabled ?? false,
    queryFn: () => {
      const queryParams = new URLSearchParams();
      if (params?.textSearch) queryParams.append('textSearch', params.textSearch);
      
      if (params?.categories) {
        if (params.categories.length === 0) {
          queryParams.append('categories', '');
        } else {
          params.categories.forEach(c => queryParams.append('categories', c));
        }
      }

      if (params?.aspectRatios) {
        params.aspectRatios.forEach(r => queryParams.append('aspectRatios', r));
      }
      
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.direction) queryParams.append('direction', params.direction);

      const queryString = queryParams.toString();
      const url = `templates/store${queryString ? `?${queryString}` : ''}`;

      return apiRequest<TemplatesListPaginatedResponse>(url);
    },
    ...options,
  });
}

/**
 * Fetches paginated store templates list (Internal) (GET /templates/internal/store/list)
 */
export function useTemplates2FindAllInternalStore(
  params?: TemplatesFilterParams,
  options?: Omit<UseQueryOptions<TemplatesListPaginatedResponse>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: ["templates", "findAllInternalStore", params],
    enabled: options?.enabled ?? false,
    queryFn: () => {
      const queryParams = new URLSearchParams();
      if (params?.textSearch) queryParams.append('textSearch', params.textSearch);
      
      if (params?.categories) {
        if (params.categories.length === 0) {
          queryParams.append('categories', '');
        } else {
          params.categories.forEach(c => queryParams.append('categories', c));
        }
      }

      if (params?.aspectRatios) {
        params.aspectRatios.forEach(r => queryParams.append('aspectRatios', r));
      }
      
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.direction) queryParams.append('direction', params.direction);

      const queryString = queryParams.toString();
      const url = `templates/store${queryString ? `?${queryString}` : ''}`;

      return apiRequest<TemplatesListPaginatedResponse>(url);
    },
    ...options,
  });
}

export function useTemplates2BulkDeleteStore() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "bulkDeleteStore"],
    mutationFn: (ids: string[]) =>
      apiRequest<void>("templates/store/bulk-delete", "POST", { ids }),
  });
}

export function useTemplates2BulkPublishStore() {
  const { apiRequest } = useApiClient();

  return useMutation({
    mutationKey: ["templates", "bulkPublishStore"],
    mutationFn: ({ ids, publish }: { ids: string[], publish: boolean }) =>
      apiRequest<void>("templates/store/bulk-publish", "POST", { ids, publish }),
  });
}

/**
 * Imports a store template (POST /templates/store/:id/import)
 */
export function useTemplates2ImportStore() {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);

  return useMutation({
    mutationKey: ["templates", "importStore"],
    mutationFn: ({ id, overrides, thumbnailData }: { id: string; overrides?: Record<string, any>; thumbnailData?: string }) =>
      apiRequest<Template>(`templates/store/${id}/import`, "POST", { overrides: overrides || {}, thumbnailData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
      void refetchUser?.();
    },
  });
}
